"use client";

import { useEffect, useRef } from "react";

/**
 * Staggered fade-up for FAQ cards as they scroll into view.
 *
 * Cards render visible and only get the `.blog-reveal` animation (defined in
 * `styles/globals.css`, `animation-fill-mode: both` + a `prefers-reduced-motion`
 * opt-out) once they intersect — so nothing is ever left hidden if JS or
 * IntersectionObserver is unavailable, which keeps the answers crawlable.
 *
 * @param resetKey change this when the rendered list changes (search, filter,
 *   language) so newly mounted cards get observed too.
 */
export function useFaqReveal<T extends HTMLElement>(resetKey: string = "") {
  const containerRef = useRef<T | null>(null);

  useEffect(() => {
    const root = containerRef.current;
    if (!root) return;
    if (typeof IntersectionObserver === "undefined") return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const el = entry.target as HTMLElement;
          const index = Number(el.dataset.faqIndex || 0);
          // Cap the stagger so a long list never waits on a long tail.
          el.style.animationDelay = `${Math.min(index, 7) * 55}ms`;
          el.classList.add("blog-reveal");
          el.dataset.faqRevealed = "1";
          observer.unobserve(el);
        });
      },
      { threshold: 0.05, rootMargin: "0px 0px -5% 0px" },
    );

    root
      .querySelectorAll<HTMLElement>(
        "[data-faq-reveal]:not([data-faq-revealed])",
      )
      .forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, [resetKey]);

  return containerRef;
}
